import React, { useState, useEffect } from "react"
import { Modal } from "react-bootstrap"
import PropTypes from "prop-types"
import { FormattedMessage } from "react-intl"

import { API_STATUS } from "../../../shared_components/widgets/utils"
import { _render_status } from "../DataExportTable"
import { logToServer } from "../../../ErrorBoundary"
import { getObjectsList } from "../../../Loaders"
import {
  CloseButton,
  LocaleFormattedDate,
  LocaleFormattedUser,
} from "../../internationalization/LocaleFormatted"
import PageLoadingPanel from "../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../shared_components/widgets/PageErrorPanel"
import { parseDate } from "../../../shared_components/utils"

export default function ModalDataExportLogs({ dataExportRq = {}, show, onHide }) {
  const [status, setStatus] = useState(API_STATUS.LOADING)
  const [dataLogs, setDataLogs] = useState([])
  const getLogs = () => {
    setStatus(API_STATUS.LOADING)
    setDataLogs([])

    getObjectsList(`/lake/data-export-logs/?data_export_request=${dataExportRq.id}&ordering=timestamp`)
      .then((d) => {
        setDataLogs(
          d.map((row) => {
            return { ...row, timestamp: parseDate(row.timestamp) }
          }),
        )
        setStatus(API_STATUS.SUCCESS)
      })
      .catch((error) => {
        console.error(error)
        logToServer(error)
        setStatus(API_STATUS.ERROR)
      })
  }
  useEffect(() => {
    getLogs()
  }, [])

  return dataExportRq ? (
    <Modal
      size={"lg"}
      dialogClassName={"common-modal"}
      show={show}
      onHide={onHide}
      style={{ overflowX: "scroll", overflowY: "scroll" }}
      backdrop={"static"}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={"fa fa-arrow-circle-o-down"} />
          &nbsp;&nbsp;
          <FormattedMessage id={"datatransfer.data.export"} description={"Data Export Title"} />-
          {dataExportRq.id} : {dataExportRq.description}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <table className={"tbl_details"}>
            <tbody>
              <tr>
                <td style={{ width: "150px" }}>
                  <b>
                    <FormattedMessage id={"general.id"} description={"ID Label"} />:
                  </b>
                </td>
                <td style={{ width: "250px" }}>{dataExportRq.id}</td>
                <td style={{ width: "150px" }}>
                  <b>
                    <FormattedMessage
                      id={"datatransfer.column.requested.by"}
                      description={"Requested By Label"}
                    />
                    :
                  </b>
                </td>
                <td style={{ width: "250px" }}>
                  <LocaleFormattedUser user={dataExportRq.requested_by} />
                </td>
              </tr>
              <tr>
                <td>
                  <b>
                    <FormattedMessage id={"general.created.on"} description={"Created On Label"} />:
                  </b>
                </td>
                <td>
                  <LocaleFormattedDate date={dataExportRq.created_on} />
                </td>
                <td>
                  <b>
                    <FormattedMessage id={"general.file"} description={"File Label"} />:
                  </b>
                </td>
                <td>
                  {dataExportRq.is_file_available ? (
                    <a
                      href={dataExportRq.datadump_file}
                      target={"_blank"}
                      style={{
                        color: "var(--lanzalake-blue-primary)",
                        textDecoration: "underline",
                      }}
                    >
                      <FormattedMessage id={"general.download"} description={"File Download Prompt"} />
                    </a>
                  ) : (
                    <></>
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <b>
                    <FormattedMessage id={"general.description"} description={"Description Label"} />:
                  </b>
                </td>
                <td>{dataExportRq.description}</td>
                <td>
                  <b>
                    <FormattedMessage id={"general.status"} description={"Status Label"} />:
                  </b>
                </td>
                <td>{_render_status(dataExportRq.status)}</td>
              </tr>
              <tr>
                <td>
                  <b>
                    <FormattedMessage
                      id={"datatransfer.column.time.period"}
                      description={"Time Period Label"}
                    />
                    :
                  </b>
                </td>
                <td>
                  <div>
                    <LocaleFormattedDate date={dataExportRq.start_datetime} /> -
                  </div>
                  <div>
                    <LocaleFormattedDate date={dataExportRq.end_datetime} />
                  </div>
                </td>
                <td>
                  <b>
                    <FormattedMessage id={"datatransfer.column.expire.on"} description={"Expire On Label"} />:
                  </b>
                </td>
                <td>
                  <LocaleFormattedDate date={dataExportRq.expire_on} />
                </td>
              </tr>
              {dataExportRq.message && (
                <tr>
                  <td>
                    <b>
                      <FormattedMessage id={"datatransfer.column.message"} description={"Message Label"} />:
                    </b>
                  </td>
                  <td colSpan={3}>
                    <div
                      style={{
                        maxWidth: "650px",
                        maxHeight: "80px",
                        overflowY: "auto",
                      }}
                    >
                      {dataExportRq.message}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <hr />
        </div>

        <h5 style={{ marginTop: "0px" }}>
          <FormattedMessage id={"general.log"} description={"Logs Table Title"} />:
        </h5>
        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
          {status === API_STATUS.LOADING ? (
            <PageLoadingPanel />
          ) : status === API_STATUS.ERROR ? (
            <PageErrorPanel attemptReload={getLogs} />
          ) : (
            status === API_STATUS.SUCCESS && (
              <table className={"table table-bordered table-sm tbl_lanzatable"}>
                <thead>
                  <tr>
                    {[
                      ["general.date", "Date"],
                      ["general.status", "Status"],
                      ["datatransfer.column.message", "Message"],
                    ].map((entry, idx) => (
                      <th key={`header${idx}`}>
                        <FormattedMessage id={entry[0]} defaultMessage={entry[1]} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataLogs.map((row, idx) => {
                    return (
                      <tr key={idx}>
                        <td>
                          <LocaleFormattedDate date={row.timestamp} />
                        </td>
                        <td>{_render_status(row.status)}</td>
                        <td>
                          <div
                            style={{
                              minWidth: "500px",
                              maxHeight: "100px",
                              overflowY: "auto",
                            }}
                          >
                            {row.message}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <CloseButton onClose={onHide} />
      </Modal.Footer>
    </Modal>
  ) : (
    <></>
  )
}

ModalDataExportLogs.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  dataExportRq: PropTypes.object.isRequired,
}
